import { useEffect, useMemo, useState } from "react";
import { Download, FileCheck2, FileText, Link2, Save, UploadCloud, X } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";


const emptyDraft = {
  contract_name: "",
  client_id: "",
  client_name: "",
  contract_start_date: "",
  contract_end_date: "",
  renewal_date: "",
  pricing_model: "hourly",
  bill_rate: "",
  annual_contract_value: "",
  staff_count: "1",
  hours_per_week: "40",
  contract_weeks: "52",
  payroll_rate: "",
  summary: "",
};


function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value || 0);
}


function initialDraft(contract) {
  return {
    contract_name: contract.contract_name || "",
    client_id: contract.client_id || "",
    client_name: contract.client_name || "",
    contract_start_date: contract.contract_start_date || "",
    contract_end_date: contract.contract_end_date || "",
    renewal_date: contract.renewal_date || "",
    pricing_model: contract.pricing_model || "hourly",
    bill_rate: String(contract.bill_rate || ""),
    annual_contract_value: String(contract.annual_contract_value || ""),
    staff_count: String(contract.staff_count || 1),
    hours_per_week: String(contract.hours_per_week || 40),
    contract_weeks: String(contract.contract_weeks || 52),
    payroll_rate: String(contract.payroll_rate || ""),
    summary: contract.summary || "",
  };
}


export default function ContractLibraryPage() {
  const { user } = useAuth();
  const canManage = user?.role === "system_administrator";
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [uploadClientId, setUploadClientId] = useState("");
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function loadContracts() {
    try {
      const [contractResponse, clientResponse] = await Promise.all([api.get("/contracts"), api.get("/contracts/clients")]);
      setContracts(contractResponse.data);
      setClients(clientResponse.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  useEffect(() => {
    loadContracts();
  }, []);

  const counts = useMemo(() => ({
    total: contracts.length,
    review: contracts.filter((item) => item.status === "Needs Review").length,
    linked: contracts.filter((item) => item.status === "Linked").length,
  }), [contracts]);

  function openReview(contract) {
    setSelected(contract);
    setDraft(initialDraft(contract));
    setError("");
    setNotice("");
  }

  async function uploadContract(event) {
    event.preventDefault();
    if (!file) {
      setError("Choose a PDF or DOCX contract to upload.");
      return;
    }
    if (!uploadClientId) {
      setError("Choose the client this contract belongs to.");
      return;
    }
    setIsUploading(true);
    setError("");
    setNotice("");
    try {
      const formData = new FormData();
      formData.append("contract_file", file);
      formData.append("client_id", uploadClientId);
      const response = await api.post("/contracts/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null);
      setNotice(
        response.data.status === "Needs Review"
          ? "Contract parsed into a draft for Administrator review."
          : "Contract saved. Its draft needs manual completion before projection linking.",
      );
      await loadContracts();
      openReview(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsUploading(false);
    }
  }

  function draftPayload() {
    return {
      ...draft,
      bill_rate: Number(draft.bill_rate || 0),
      annual_contract_value: Number(draft.annual_contract_value || 0),
      staff_count: Number(draft.staff_count || 1),
      hours_per_week: Number(draft.hours_per_week || 0),
      contract_weeks: Number(draft.contract_weeks || 52),
      payroll_rate: Number(draft.payroll_rate || 0),
    };
  }

  async function saveDraft(event) {
    event.preventDefault();
    if (!selected) {
      return;
    }
    setIsSaving(true);
    setError("");
    try {
      const response = await api.patch(`/contracts/${selected.id}`, draftPayload());
      setSelected(response.data);
      setDraft(initialDraft(response.data));
      setNotice("Contract draft saved. It is ready to link when the pricing is complete.");
      await loadContracts();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  async function linkToProjection() {
    if (!selected) {
      return;
    }
    setIsSaving(true);
    setError("");
    try {
      await api.post(`/contracts/${selected.id}/link-projection`);
      setNotice("Approved terms are now linked to the Annual Contract Outlook.");
      setSelected({ ...selected, status: "Linked" });
      await loadContracts();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  async function downloadContract(contract) {
    try {
      const response = await api.get(`/contracts/${contract.id}/download`, { responseType: "blob" });
      const objectUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = contract.original_filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <div className="app-shell" data-testid="contract-library-shell">
      <AppSidebar activeItem="Contract Library" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page contract-library-page" data-testid="contract-library-page">
          <div className="page-intro">
            <div>
              <p className="eyebrow">INTERNAL COMMERCIAL RECORDS</p>
              <h1>Contract Library</h1>
              <p className="page-subtitle">
                Protected contract records, structured into reviewable planning drafts.
              </p>
            </div>
          </div>

          {error && <p className="form-message error-message" data-testid="contract-error-message">{error}</p>}
          {notice && <p className="form-message success-message" data-testid="contract-success-message">{notice}</p>}

          <section className="contract-summary-grid" data-testid="contract-library-summary">
            <article data-testid="contracts-total-count"><small>Contract records</small><strong>{counts.total}</strong></article>
            <article data-testid="contracts-review-count"><small>Needs Administrator review</small><strong>{counts.review}</strong></article>
            <article data-testid="contracts-linked-count"><small>Linked to outlook</small><strong>{counts.linked}</strong></article>
          </section>

          {canManage && (
            <section className="contract-upload-panel" data-testid="contract-upload-panel">
              <div>
                <p className="eyebrow">NEW CONTRACT</p>
                <h2>Upload for structured review</h2>
                <p>PDF and DOCX contracts are securely saved and parsed into a draft before they affect planning.</p>
              </div>
              <form onSubmit={uploadContract} className="contract-upload-form" data-testid="contract-upload-form">
                <label>
                  Client
                  <select data-testid="contract-upload-client-select" value={uploadClientId} onChange={(event) => setUploadClientId(event.target.value)} required>
                    <option value="">Choose a client…</option>
                    {clients.map((client) => <option key={client.id} value={client.id}>{client.name}{client.active ? "" : " (inactive)"}</option>)}
                  </select>
                </label>
                <label>
                  Contract file
                  <input
                    accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    data-testid="contract-file-input"
                    onChange={(event) => setFile(event.target.files?.[0] || null)}
                    type="file"
                    required
                  />
                </label>
                <button
                  className="button button-primary"
                  data-testid="upload-contract-button"
                  disabled={isUploading || !uploadClientId}
                >
                  <UploadCloud size={17} />
                  {isUploading ? "Parsing contract…" : "Upload & parse"}
                </button>
              </form>
            </section>
          )}

          <section className="contract-records-panel" data-testid="contract-records-panel">
            <div className="section-heading">
              <div>
                <h2>Contract records</h2>
                <p>{contracts.length} documents in the internal library</p>
              </div>
            </div>
            <div className="contract-record-list">
              {contracts.map((contract) => (
                <article className="contract-record-row" key={contract.id} data-testid={`contract-row-${contract.id}`}>
                  <span className="contract-file-icon"><FileText size={19} /></span>
                  <div className="contract-record-main">
                    <strong>{contract.contract_name || contract.original_filename}</strong>
                    <span>{contract.client_name || "Client to be confirmed"} · Uploaded {new Date(contract.created_at).toLocaleDateString()}{!contract.client_id && <em className="contract-client-tag" data-testid={`contract-no-client-${contract.id}`}>No client linked</em>}{contract.client_active === false && <em className="contract-client-tag" data-testid={`contract-inactive-client-${contract.id}`}>Inactive client</em>}{contract.term_ended && <em className="contract-client-tag" data-testid={`contract-term-ended-${contract.id}`}>Term ended</em>}</span>
                  </div>
                  <span className={`contract-status contract-status-${(contract.status || "Needs Review").toLowerCase().replaceAll(" ", "-")}`}>
                    {contract.status || "Needs Review"}
                  </span>
                  <div className="contract-row-actions">
                    <button
                      className="projection-icon-button"
                      data-testid={`download-contract-${contract.id}`}
                      type="button"
                      onClick={() => downloadContract(contract)}
                      aria-label={`Download ${contract.original_filename}`}
                    >
                      <Download size={15} />
                    </button>
                    <button
                      className="button button-secondary"
                      data-testid={`review-contract-${contract.id}`}
                      type="button"
                      onClick={() => openReview(contract)}
                    >
                      <FileCheck2 size={15} />
                      {canManage ? "Review" : "View draft"}
                    </button>
                  </div>
                </article>
              ))}
              {!contracts.length && (
                <p className="contract-empty-state" data-testid="contracts-empty-state">
                  No contract records yet. Upload the first approved commercial agreement to begin.
                </p>
              )}
            </div>
          </section>

          {selected && (
            <section className="contract-review-panel" data-testid="contract-review-panel">
              <div className="contract-review-heading">
                <div>
                  <p className="eyebrow">{selected.status.toUpperCase()}</p>
                  <h2>Review contract draft</h2>
                  <p>Confirm the extracted terms before linking this contract to the dashboard forecast.</p>
                </div>
                <button
                  className="projection-icon-button"
                  data-testid="close-contract-review-button"
                  type="button"
                  onClick={() => setSelected(null)}
                  aria-label="Close contract review"
                >
                  <X size={17} />
                </button>
              </div>
              {selected.parse_error && <p className="form-message error-message" data-testid="contract-parse-error">{selected.parse_error}</p>}
              {canManage ? (
                <form className="contract-review-form" onSubmit={saveDraft}>
                  <label>Contract name<input data-testid="contract-name-input" value={draft.contract_name} onChange={(event) => setDraft({ ...draft, contract_name: event.target.value })} required /></label>
                  <label>Client<select data-testid="contract-client-select" value={draft.client_id} onChange={(event) => setDraft({ ...draft, client_id: event.target.value, client_name: clients.find((client) => client.id === event.target.value)?.name || draft.client_name })} required><option value="">Choose a client…</option>{clients.map((client) => <option key={client.id} value={client.id}>{client.name}{client.active ? "" : " (inactive)"}</option>)}</select></label>
                  <label>Start date<input data-testid="contract-start-date-input" type="date" value={draft.contract_start_date} onChange={(event) => setDraft({ ...draft, contract_start_date: event.target.value })} /></label>
                  <label>End date<input data-testid="contract-end-date-input" type="date" value={draft.contract_end_date} onChange={(event) => setDraft({ ...draft, contract_end_date: event.target.value })} /></label>
                  <label>Renewal date<input data-testid="contract-renewal-date-input" type="date" value={draft.renewal_date} onChange={(event) => setDraft({ ...draft, renewal_date: event.target.value })} /></label>
                  <label>Pricing model<select data-testid="contract-pricing-model-select" value={draft.pricing_model} onChange={(event) => setDraft({ ...draft, pricing_model: event.target.value })}><option value="hourly">Hourly bill rate</option><option value="annual_value">Annual contract value</option></select></label>
                  <label>Staff count<input data-testid="contract-staff-count-input" min="1" type="number" value={draft.staff_count} onChange={(event) => setDraft({ ...draft, staff_count: event.target.value })} required /></label>
                  <label>Hours per week<input data-testid="contract-hours-week-input" min="0" step="0.5" type="number" value={draft.hours_per_week} onChange={(event) => setDraft({ ...draft, hours_per_week: event.target.value })} required /></label>
                  <label>Contract weeks<input data-testid="contract-weeks-input" min="1" max="52" type="number" value={draft.contract_weeks} onChange={(event) => setDraft({ ...draft, contract_weeks: event.target.value })} required /></label>
                  {draft.pricing_model === "hourly" ? <label>Bill rate / hour<input data-testid="contract-bill-rate-input" min="0" step="0.01" type="number" value={draft.bill_rate} onChange={(event) => setDraft({ ...draft, bill_rate: event.target.value })} required /></label> : <label>Annual contract value<input data-testid="contract-annual-value-input" min="0" step="0.01" type="number" value={draft.annual_contract_value} onChange={(event) => setDraft({ ...draft, annual_contract_value: event.target.value })} required /></label>}
                  <label>Payroll cost / hour<input data-testid="contract-payroll-rate-input" min="0" step="0.01" type="number" value={draft.payroll_rate} onChange={(event) => setDraft({ ...draft, payroll_rate: event.target.value })} required /></label>
                  <label className="contract-review-full">Commercial summary<textarea data-testid="contract-summary-input" rows="4" value={draft.summary} onChange={(event) => setDraft({ ...draft, summary: event.target.value })} /></label>
                  <div className="contract-review-actions contract-review-full">
                    <button className="button button-secondary" data-testid="save-contract-draft-button" disabled={isSaving}><Save size={16} /> Save reviewed draft</button>
                    <button className="button button-primary" data-testid="link-contract-projection-button" disabled={isSaving || selected.status === "Linked"} onClick={linkToProjection} type="button"><Link2 size={16} /> {selected.status === "Linked" ? "Linked to outlook" : "Link to outlook"}</button>
                  </div>
                </form>
              ) : (
                <ContractDraftReadOnly contract={selected} />
              )}
            </section>
          )}
        </main>
      </div>
    </div>
  );
}


function ContractDraftReadOnly({ contract }) {
  return (
    <dl className="contract-read-only" data-testid="contract-read-only-draft">
      <div><dt>Client</dt><dd>{contract.client_name || "Not confirmed"}</dd></div>
      <div><dt>Term</dt><dd>{contract.contract_start_date || "—"} to {contract.contract_end_date || "—"}</dd></div>
      <div><dt>Pricing</dt><dd>{contract.pricing_model === "annual_value" ? formatCurrency(contract.annual_contract_value) : `${formatCurrency(contract.bill_rate)}/hour`}</dd></div>
      <div><dt>Staffing</dt><dd>{contract.staff_count || 0} staff · {contract.hours_per_week || 0} hrs/week</dd></div>
      <div className="contract-read-only-full"><dt>Summary</dt><dd>{contract.summary || "No parser summary available."}</dd></div>
    </dl>
  );
}